import { useMemo, useState } from 'react'

import {
  Modal,
  ModalContent,
  ModalDismiss,
  ModalFooter,
  ModalHeader,
  ModalHeading,
  Text
} from '@vtex/shoreline'

import { Button } from '~/components/Buttons'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import { OptionSelect } from '../../../components/advertiserBrand/OptionSelect'
import { ProductPicker } from '../../../components/advertiserBrand/ProductPicker'
import {
  ProductCandidate,
  ProductSelection,
  toProductInput
} from '../../../components/advertiserBrand/ProductPicker/types'
import styles from '../../../components/advertiserBrand/styles.module.css'
import {
  groupRowsByBrand,
  useBrandAssignments
} from '../../../components/advertiserBrand/useBrandAssignments'
import { useAdvertiserBrandProductMutations } from '../../../hooks/useAdvertiserBrandProducts'
import { useAdvertiserBrandOptions } from '../../../hooks/useAdvertiserBrands'

type AddProductsModalProps = {
  open: boolean
  defaultBrand?: string
  onClose: () => void
}

const getKey = (row: ProductCandidate) => row.key

export const AddProductsModal: React.FC<AddProductsModalProps> = ({
  open,
  defaultBrand,
  onClose
}) => {
  const { t } = useAPPTranslation()
  const { options: brandOptions } = useAdvertiserBrandOptions()
  const { addProductsBatch } = useAdvertiserBrandProductMutations()

  const [selected, setSelected] = useState<ProductSelection>({})

  /** Nome que só difere na caixa de uma marca existente é essa marca. */
  const toBrand = (typed: string | null) => {
    const name = typed?.trim()

    if (!name) return null

    const existing = brandOptions.find(
      option => option.value.toLowerCase() === name.toLowerCase()
    )

    return existing?.value ?? name
  }

  /**
   * A marca da linha do arquivo, na aba de lista; senão a que veio com a linha
   * ou a do deep link. A lista entra sem marca quando o arquivo não traz uma.
   */
  const assignments = useBrandAssignments(getKey, (row: ProductCandidate) => {
    if (row.fileBrand) return toBrand(row.fileBrand)

    return row.origin === 'batch'
      ? null
      : (row.suggestedBrand ?? defaultBrand ?? null)
  })

  const selectedRows = Object.values(selected)

  const existingBrands = useMemo(
    () => new Set(brandOptions.map(option => option.value)),
    [brandOptions]
  )

  /**
   * Um nome só por marca, sem diferença de caixa ou espaço: a marca existente
   * vale sobre a digitada, e entre duas digitadas vale a primeira.
   */
  const canonicalBrands = new Map(
    brandOptions.map(option => [option.value.toLowerCase(), option.value])
  )

  selectedRows.forEach(row => {
    const brand = toBrand(assignments.brandFor(row))

    if (brand && !canonicalBrands.has(brand.toLowerCase()))
      canonicalBrands.set(brand.toLowerCase(), brand)
  })

  const brandOf = (row: ProductCandidate) => {
    const brand = toBrand(assignments.brandFor(row))

    return brand ? (canonicalBrands.get(brand.toLowerCase()) ?? brand) : null
  }

  /** Marca digitada que não existe entra como opção para as outras linhas. */
  const options = [
    ...brandOptions,
    ...[...canonicalBrands.values()]
      .filter(brand => !existingBrands.has(brand))
      .map(brand => ({ label: brand, value: brand }))
  ]

  const handleClose = () => {
    setSelected({})
    assignments.reset()
    onClose()
  }

  /** Sugestão aceita sem revisão, marca do arquivo sem troca, ou escolha da pessoa. */
  const sourceFor = (row: ProductCandidate): AdvertiserBrandSource => {
    if (row.suggestedBrand && assignments.isSuggested(row))
      return 'system_suggestion'

    return row.fileBrand && !assignments.isTouched(row) ? 'csv' : 'catalog'
  }

  const toInput = (row: ProductCandidate) => toProductInput(row, sourceFor(row))

  /** Linha sem marca resolvida entra do mesmo jeito: fica listada em "Meus produtos", sem marca. */
  const handleSubmit = async () => {
    const { byBrand, unresolved } = groupRowsByBrand(selectedRows, brandOf)

    await addProductsBatch.mutateAsync([
      ...Object.entries(byBrand).map(([brand, rows]) => ({
        brand,
        products: rows.map(toInput),
        isNewBrand: !existingBrands.has(brand)
      })),
      ...(unresolved.length ? [{ products: unresolved.map(toInput) }] : [])
    ])

    handleClose()
  }

  /** Sempre um select limpável: dá para tirar a marca que veio sugerida e deixar sem. */
  const renderBrandCell = (row: ProductCandidate) => (
    <OptionSelect
      placeholder={t('rm:advertiserProducts.noBrand')}
      options={options}
      createLabel={name =>
        t('rm:advertiserProducts.addModal.brand.create', { name })
      }
      tone={
        row.suggestedBrand && assignments.isSuggested(row)
          ? 'suggested'
          : 'default'
      }
      value={brandOf(row)}
      onChange={option =>
        assignments.assign(row, toBrand(option?.value ?? null))
      }
    />
  )

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="large"
      className={styles.pickerModal}
    >
      <ModalHeader>
        <div className={styles.modalHeading}>
          <ModalHeading>
            {t('rm:advertiserProducts.addModal.title')}
          </ModalHeading>
          <Text variant="body" className={styles.description}>
            {t('rm:advertiserProducts.addModal.subtitle')}
          </Text>
        </div>
        <ModalDismiss />
      </ModalHeader>

      <ModalContent className={styles.pickerModalContent}>
        <ProductPicker
          open={open}
          selected={selected}
          onSelectedChange={setSelected}
          renderAside={renderBrandCell}
        />
      </ModalContent>

      <ModalFooter>
        <Button template="transparent" size="medium" onClick={handleClose}>
          {t('rm:button.Cancel')}
        </Button>
        <Button
          template="primary"
          size="medium"
          disabled={!selectedRows.length}
          loading={addProductsBatch.isPending}
          onClick={handleSubmit}
        >
          {t('rm:advertiserProducts.addModal.submit', {
            count: selectedRows.length
          })}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
